<script lang="ts" setup>
import { computed, watch, toRef, onMounted, reactive } from 'vue'
import { useGeo, usePostcodeState } from '@/composables'
import { useValidationRules } from '@/composables/useValidationRules'
import { all as getAllCountries, type CountryData } from 'country-codes-list'

export interface AddressInfoData {
  line1: string
  line2: string
  postcode: string
  city: string
  country_iso: string
  state: string | undefined
}

const props = defineProps<{
  modelValue: AddressInfoData
  disabled: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: AddressInfoData): void
  (e: 'countryChanged', countryCode: string): void
}>()

// Validation
const { required, postcodeDetailed } = useValidationRules()

const errors = reactive({
  line1: '' as string | true,
  postcode: '' as string | true,
  city: '' as string | true
})

const touched = reactive({
  line1: false,
  postcode: false,
  city: false
})

const validateField = (field: 'line1' | 'postcode' | 'city', value: string): string | true => {
  if (field === 'postcode') {
    // Only validate German postcodes strictly
    if (props.modelValue.country_iso?.toLowerCase() === 'de') {
      return postcodeDetailed(value)
    }
    return required(value)
  }
  return required(value)
}

const handleBlur = (field: 'line1' | 'postcode' | 'city') => {
  touched[field] = true
  const value = field === 'line1' ? props.modelValue.line1 :
                field === 'postcode' ? props.modelValue.postcode :
                props.modelValue.city
  errors[field] = validateField(field, value)
}

const getError = (field: 'line1' | 'postcode' | 'city'): string => {
  const error = errors[field]
  return typeof error === 'string' && error !== '' ? error : ''
}

// Country codes data for address country selection
const countryCodesData = computed(() => {
  return getAllCountries().sort((a, b) => a.countryNameEn.localeCompare(b.countryNameEn))
})

// Find current country data object
const currentCountryData = computed(() => {
  return (
    countryCodesData.value.find(
      (c) => c.countryCode.toLowerCase() === props.modelValue.country_iso?.toLowerCase()
    ) || null
  )
})

// Geo data for subdivisions
const { subdivisions, loadSubdivisions, subdivisionsLoading } = useGeo()

// Postcode state suggestion - stateRef must be writable for usePostcodeState to update it
const countryCodeRef = computed(() => currentCountryData.value?.countryCode || 'DE')
const cityRef = toRef(() => props.modelValue.city)
const stateRef = computed({
  get: () => props.modelValue.state,
  set: (val) => {
    emit('update:modelValue', {
      ...props.modelValue,
      state: val
    })
  }
})
const { fetchStateFromPostcode } = usePostcodeState(stateRef, {
  onlyWhenEmpty: true,
  countryRef: countryCodeRef,
  cityRef,
  useCode: true
})

// Load subdivisions when country changes
watch(
  () => currentCountryData.value?.countryCode,
  (newCountry) => {
    if (newCountry) {
      loadSubdivisions(newCountry)
    }
  },
  { immediate: true }
)

// Normalize state from name to code when subdivisions are loaded
// Handles legacy data stored as full name (e.g., "Nordrhein-Westfalen") instead of code ("NW")
watch(
  () => subdivisions.value,
  (subs) => {
    const currentState = props.modelValue.state
    if (!currentState || !subs.length) return

    // Check if state is already a valid code
    const isCode = subs.some((s) => s.code === currentState)
    if (isCode) return

    // Try to find by name and convert to code
    const matchByName = subs.find(
      (s) => s.name.toLowerCase() === currentState.toLowerCase()
    )
    if (matchByName) {
      emit('update:modelValue', {
        ...props.modelValue,
        state: matchByName.code
      })
    }
  },
  { immediate: true }
)

// Load subdivisions on mount for initial country
onMounted(() => {
  const countryCode = currentCountryData.value?.countryCode || 'DE'
  loadSubdivisions(countryCode)
})

const updateField = <K extends keyof AddressInfoData>(field: K, value: AddressInfoData[K]) => {
  emit('update:modelValue', {
    ...props.modelValue,
    [field]: value
  })

  // Real-time validation for touched fields
  const validatableFields: Array<'line1' | 'postcode' | 'city'> = ['line1', 'postcode', 'city']
  if (validatableFields.includes(field as 'line1' | 'postcode' | 'city') && touched[field as 'line1' | 'postcode' | 'city']) {
    errors[field as 'line1' | 'postcode' | 'city'] = validateField(field as 'line1' | 'postcode' | 'city', value as string)
  }
}

const handleCountryChange = (country: CountryData | null) => {
  if (country) {
    const newData = {
      ...props.modelValue,
      country_iso: country.countryCode.toLowerCase(),
      state: '' // Clear state when country changes
    }
    emit('update:modelValue', newData)
    emit('countryChanged', country.countryCode)
    loadSubdivisions(country.countryCode)
  }
}

const handlePostcodeBlur = () => {
  handleBlur('postcode')
  if (props.modelValue.postcode) {
    fetchStateFromPostcode(props.modelValue.postcode)
  }
}

</script>

<template>
  <div class="form-section">
    <h3 class="form-section-title">
      <v-icon icon="mdi-map-marker" size="18" />
      <span>{{ $t('dashboard.account.stammdaten.address') }}</span>
    </h3>

    <div class="form-row">
      <div class="form-group flex-2">
        <label class="form-label">{{ $t('dashboard.account.stammdaten.street') }}</label>
        <input
          :value="modelValue.line1"
          type="text"
          class="form-input"
          :disabled="disabled"
          :class="{ disabled: disabled, 'has-error': getError('line1') }"
          :placeholder="$t('dashboard.account.stammdaten.streetAndNumber')"
          @input="updateField('line1', ($event.target as HTMLInputElement).value)"
          @blur="handleBlur('line1')"
        />
        <Transition name="error-slide">
          <span v-if="getError('line1')" class="field-error">{{ getError('line1') }}</span>
        </Transition>
      </div>
      <div class="form-group flex-1">
        <label class="form-label">{{ $t('dashboard.account.stammdaten.addressSupplement') }}</label>
        <input
          :value="modelValue.line2"
          type="text"
          class="form-input"
          :disabled="disabled"
          :class="{ disabled: disabled }"
          :placeholder="$t('dashboard.account.stammdaten.optional')"
          @input="updateField('line2', ($event.target as HTMLInputElement).value)"
        />
      </div>
    </div>

    <div class="form-row">
      <div class="form-group">
        <label class="form-label">{{ $t('dashboard.account.stammdaten.country') }}</label>
        <v-autocomplete
          :model-value="currentCountryData"
          :items="countryCodesData"
          :disabled="disabled"
          density="compact"
          item-title="countryNameEn"
          item-value="countryCode"
          return-object
          variant="outlined"
          hide-details
          :class="{ disabled: disabled }"
          :menu-props="{ maxHeight: 300 }"
          @update:model-value="handleCountryChange"
        >
          <template #selection="{ item }">
            <span class="country-selection">
              <span class="flag">{{ item.flag }}</span>
              <span>{{ item.countryNameEn }}</span>
            </span>
          </template>
          <template #item="{ props: itemProps, item }">
            <v-list-item v-bind="itemProps" class="country-item">
              <template #prepend>
                <span class="flag-large">{{ item.flag }}</span>
              </template>
              <template #title>{{ item.countryNameEn }}</template>
              <template #subtitle>{{ item.countryCode }}</template>
            </v-list-item>
          </template>
        </v-autocomplete>
      </div>
      <div class="form-group">
        <label class="form-label">{{ $t('dashboard.account.stammdaten.postcode') }}</label>
        <input
          :value="modelValue.postcode"
          type="text"
          class="form-input"
          :disabled="disabled"
          :class="{ disabled: disabled, 'has-error': getError('postcode') }"
          :placeholder="$t('dashboard.account.stammdaten.postalCode')"
          @input="updateField('postcode', ($event.target as HTMLInputElement).value)"
          @blur="handlePostcodeBlur"
        />
        <Transition name="error-slide">
          <span v-if="getError('postcode')" class="field-error">{{ getError('postcode') }}</span>
        </Transition>
      </div>
    </div>

    <div class="form-row">
      <div class="form-group flex-2">
        <label class="form-label">{{ $t('dashboard.account.stammdaten.city') }}</label>
        <input
          :value="modelValue.city"
          type="text"
          class="form-input"
          :disabled="disabled"
          :class="{ disabled: disabled, 'has-error': getError('city') }"
          :placeholder="$t('dashboard.account.stammdaten.city')"
          @input="updateField('city', ($event.target as HTMLInputElement).value)"
          @blur="handleBlur('city')"
        />
        <Transition name="error-slide">
          <span v-if="getError('city')" class="field-error">{{ getError('city') }}</span>
        </Transition>
      </div>
      <div class="form-group">
        <label class="form-label">{{ $t('dashboard.account.stammdaten.state') }}</label>
        <v-autocomplete
          :model-value="modelValue.state"
          :items="subdivisions"
          :disabled="disabled || subdivisionsLoading"
          :loading="subdivisionsLoading"
          density="compact"
          item-title="name"
          item-value="code"
          variant="outlined"
          hide-details
          clearable
          :class="{ disabled: disabled }"
          :menu-props="{ maxHeight: 300 }"
          :placeholder="
            subdivisionsLoading ? 'Laden...' : $t('dashboard.account.stammdaten.state')
          "
          @update:model-value="updateField('state', $event)"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.form-section {
  margin-bottom: 32px;
  padding-bottom: 28px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);

  &:last-of-type {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
}

.form-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.7);
  margin: 0 0 20px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &.flex-1 {
    flex: 1;
  }

  &.flex-2 {
    flex: 2;
  }
}

.form-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.form-input {
  width: 100%;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.95);
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.3);
  }

  &:focus {
    outline: none;
    border-color: #00b0ff;
    background: rgba(0, 176, 255, 0.05);
    box-shadow: 0 0 0 3px rgba(0, 176, 255, 0.1);
  }

  &:disabled,
  &.disabled {
    background: rgba(255, 255, 255, 0.02);
    border-color: rgba(120, 163, 255, 0.08);
    color: rgba(255, 255, 255, 0.6);
    cursor: not-allowed;
  }

  &.has-error {
    border-color: #ef5350;
    background: rgba(244, 67, 54, 0.05);

    &:focus {
      border-color: #ef5350;
      box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.1);
    }
  }
}

/* Field Error */
.field-error {
  display: block;
  font-size: 0.75rem;
  color: #ef5350;
  margin-top: 4px;
}

/* Error Transition */
.error-slide-enter-active {
  transition: all 0.2s ease;
}

.error-slide-leave-active {
  transition: all 0.15s ease;
}

.error-slide-enter-from,
.error-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Country Select */
.country-selection {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
  min-width: 0;

  span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}


.flag {
  font-size: 1.1rem;
}

.flag-large {
  font-size: 1.5rem;
  margin-right: 8px;
}

.country-item {
  padding: 8px 12px;
}


/* Light Theme */
.v-theme--lightTheme {
  .form-section {
    border-color: rgba(28, 110, 227, 0.08);
  }

  .form-section-title {
    color: rgba(15, 23, 42, 0.6);
  }

  .form-label {
    color: rgba(15, 23, 42, 0.6);
  }

  .form-input {
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(15, 23, 42, 0.95);

    &::placeholder {
      color: rgba(15, 23, 42, 0.3);
    }

    &:focus {
      border-color: #1c6ee3;
      background: rgba(28, 110, 227, 0.03);
      box-shadow: 0 0 0 3px rgba(28, 110, 227, 0.1);
    }

    &:disabled,
    &.disabled {
      background: rgba(0, 0, 0, 0.02);
      color: rgba(15, 23, 42, 0.5);
    }

    &.has-error {
      border-color: #d32f2f;
      background: rgba(211, 47, 47, 0.03);

      &:focus {
        border-color: #d32f2f;
        box-shadow: 0 0 0 3px rgba(211, 47, 47, 0.1);
      }
    }
  }

  .field-error {
    color: #d32f2f;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .form-input {
    transition: none;
  }
}
</style>

<!-- Unscoped styles for Vuetify components - SSR compatibility -->
<style lang="scss">
/* Prevent v-autocomplete from becoming multi-line when focused */
.form-section .v-field__input {
  flex-wrap: nowrap !important;
  overflow: hidden;
}

.form-section .v-autocomplete__selection {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

/* Base v-autocomplete styling */
.form-section .v-autocomplete .v-field {
  background: rgba(0, 0, 0, 0.2) !important;
  border-radius: 10px !important;
}

.form-section .v-autocomplete .v-field__input {
  padding: 10px 14px;
  min-height: 44px;
}

/* Override Vuetify outline border colors */
.form-section .v-autocomplete .v-field__outline__start,
.form-section .v-autocomplete .v-field__outline__end,
.form-section .v-autocomplete .v-field__outline__notch::before,
.form-section .v-autocomplete .v-field__outline__notch::after {
  border-color: rgba(120, 163, 255, 0.15) !important;
}

/* Focus state */
.form-section .v-autocomplete .v-field--focused .v-field__outline__start,
.form-section .v-autocomplete .v-field--focused .v-field__outline__end,
.form-section .v-autocomplete .v-field--focused .v-field__outline__notch::before,
.form-section .v-autocomplete .v-field--focused .v-field__outline__notch::after {
  border-color: #00b0ff !important;
}

/* Autocomplete disabled state */
.form-section .v-autocomplete.disabled .v-field {
  background: rgba(255, 255, 255, 0.02) !important;
}

.form-section .v-autocomplete.disabled .v-field__outline__start,
.form-section .v-autocomplete.disabled .v-field__outline__end,
.form-section .v-autocomplete.disabled .v-field__outline__notch::before,
.form-section .v-autocomplete.disabled .v-field__outline__notch::after {
  border-color: rgba(120, 163, 255, 0.08) !important;
}

/* Light Theme */
.v-theme--lightTheme .form-section .v-autocomplete .v-field {
  background: rgba(0, 0, 0, 0.03) !important;
}

.v-theme--lightTheme .form-section .v-autocomplete .v-field__outline__start,
.v-theme--lightTheme .form-section .v-autocomplete .v-field__outline__end,
.v-theme--lightTheme .form-section .v-autocomplete .v-field__outline__notch::before,
.v-theme--lightTheme .form-section .v-autocomplete .v-field__outline__notch::after {
  border-color: rgba(28, 110, 227, 0.15) !important;
}

.v-theme--lightTheme .form-section .v-autocomplete .v-field--focused .v-field__outline__start,
.v-theme--lightTheme .form-section .v-autocomplete .v-field--focused .v-field__outline__end,
.v-theme--lightTheme .form-section .v-autocomplete .v-field--focused .v-field__outline__notch::before,
.v-theme--lightTheme .form-section .v-autocomplete .v-field--focused .v-field__outline__notch::after {
  border-color: #1c6ee3 !important;
}

.v-theme--lightTheme .form-section .v-autocomplete.disabled .v-field {
  background: rgba(0, 0, 0, 0.02) !important;
}
</style>
